import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Trophy, Flame, Crown, Medal } from "lucide-react";

interface Player {
  userId: string;
  name: string;
  points: number;
  streak: number;
  todayPct: number;
  isMe: boolean;
  rank: number;
}

interface ScoreboardScreenProps {
  scoreboard: Player[];
  period: string;
  onPeriodChange: (period: string) => void;
}

const RANK_COLORS = ["#E8A020", "#C0C0C0", "#CD7F32"];
const RANK_ICONS = [Crown, Medal, Medal];

export function ScoreboardScreen({ scoreboard, period, onPeriodChange }: ScoreboardScreenProps) {
  const top3 = scoreboard.slice(0, 3);

  return (
    <div className="pb-24 px-4 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Trophy size={22} className="text-gold" />
          Skor Tablosu
        </h1>
      </div>

      {/* Period toggle */}
      <div className="flex bg-muted rounded-xl p-1 mb-6">
        {[
          { id: "weekly", label: "Haftalik" },
          { id: "monthly", label: "Aylik" },
          { id: "alltime", label: "Tum Zamanlar" },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => onPeriodChange(id)}
            className={cn(
              "flex-1 py-2 rounded-lg text-xs font-semibold transition-all",
              period === id
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Podium */}
      {top3.length >= 3 && (
        <div className="flex items-end justify-center gap-3 mb-8">
          {/* 2nd place */}
          <PodiumCard player={top3[1]} height="h-28" rank={2} />
          {/* 1st place */}
          <PodiumCard player={top3[0]} height="h-36" rank={1} />
          {/* 3rd place */}
          <PodiumCard player={top3[2]} height="h-24" rank={3} />
        </div>
      )}

      {/* Full ranking */}
      <div className="space-y-2">
        {scoreboard.map((player) => (
          <Card
            key={player.userId}
            className={cn(
              "transition-all",
              player.isMe && "border-gold/50 bg-gold/5 ring-1 ring-gold/20",
            )}
          >
            <CardContent className="p-3 flex items-center gap-3">
              {/* Rank */}
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0",
                player.rank <= 3
                  ? "text-white"
                  : "bg-muted text-muted-foreground",
              )} style={player.rank <= 3 ? { backgroundColor: RANK_COLORS[player.rank - 1] } : {}}>
                {player.rank}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn("font-semibold text-sm truncate", player.isMe && "text-gold")}>
                    {player.name}
                  </span>
                  {player.isMe && <Badge variant="warning" className="text-[9px] h-4">Sen</Badge>}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {player.streak > 0 && (
                    <span className="text-[10px] text-ehille-orange flex items-center gap-0.5">
                      <Flame size={10} /> {player.streak}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    Bugun: {player.todayPct}%
                  </span>
                </div>
              </div>

              {/* Points */}
              <div className="text-right shrink-0">
                <span className="font-bold text-sm">{player.points}</span>
                <p className="text-[9px] text-muted-foreground">puan</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function PodiumCard({ player, height, rank }: { player: Player; height: string; rank: number }) {
  const Icon = RANK_ICONS[rank - 1] ?? Medal;
  return (
    <div className="flex flex-col items-center flex-1">
      {/* Avatar + crown */}
      <div className="relative mb-2">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white"
          style={{ backgroundColor: RANK_COLORS[rank - 1] + "40", border: `2px solid ${RANK_COLORS[rank - 1]}` }}
        >
          {player.name.charAt(0)}
        </div>
        <div
          className="absolute -top-2 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: RANK_COLORS[rank - 1] }}
        >
          <Icon size={12} className="text-white" />
        </div>
      </div>
      <span className={cn("text-xs font-bold truncate max-w-[80px] text-center", player.isMe && "text-gold")}>
        {player.name}
      </span>
      <span className="text-[10px] text-muted-foreground">{player.points} puan</span>
      {/* Podium bar */}
      <div
        className={cn("w-full rounded-t-xl mt-2", height)}
        style={{
          background: `linear-gradient(180deg, ${RANK_COLORS[rank - 1]}30 0%, ${RANK_COLORS[rank - 1]}10 100%)`,
          borderTop: `3px solid ${RANK_COLORS[rank - 1]}`,
        }}
      />
    </div>
  );
}
